/**
 * Unified VisitStart known-record path for allergies / medications / demographics.
 * Sources (tci-session consent load, inbound CMR bridge, classic share-code
 * redemption, e2e test hook) write into the same shape so confirm-or-correct
 * UI does not care where the data came from. When two sources provide the
 * same field, the most recently obtained value wins.
 */

export const KNOWN_RECORD_SOURCES = Object.freeze({
  TCI_SESSION: "tci-session",
  CMR_BRIDGE: "cmr-bridge",
  SHARE_CODE: "share-code",
  TEST_HOOK: "test-hook",
});

export function emptyKnownRecord() {
  return {
    allergies: null,
    medications: null,
    demographics: null,
  };
}

function asList(value) {
  if (value == null) return undefined;
  return Array.isArray(value) ? value : undefined;
}

function asDemographics(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const firstName = String(value.firstName || value.name || "").trim();
  const lastName = String(value.lastName || "").trim();
  const dateOfBirth = String(value.dateOfBirth || value.dob || "").trim();
  const displayName = String(value.displayName || "").trim();
  const sex = String(value.sex || "").trim().toLowerCase();
  if (!firstName && !lastName && !dateOfBirth && !displayName && !sex) return undefined;
  return {
    firstName,
    lastName,
    dateOfBirth,
    sex,
    displayName: displayName || [firstName, lastName].filter(Boolean).join(" ").trim(),
  };
}

/**
 * Apply one source onto the unified record. Fields omitted (undefined) are
 * left unchanged. Explicit null / [] is data ("we know this is empty").
 */
export function applyKnownRecordSource(current, source = {}) {
  const next = {
    allergies: current?.allergies ?? null,
    medications: current?.medications ?? null,
    demographics: current?.demographics ?? null,
  };
  const obtainedAt = Number(source.obtainedAt) || Date.now();
  const sourceId = source.sourceId || "unknown";

  const maybeApply = (field, raw) => {
    if (raw === undefined) return;
    const existing = next[field];
    if (existing && Number(existing.obtainedAt) > obtainedAt) return;
    next[field] = { value: raw, obtainedAt, source: sourceId };
  };

  if (Object.prototype.hasOwnProperty.call(source, "allergies")) {
    const list = source.allergies === null ? null : asList(source.allergies);
    if (source.allergies === null || list !== undefined) {
      maybeApply("allergies", list === undefined ? null : list);
    }
  }
  if (Object.prototype.hasOwnProperty.call(source, "medications")) {
    const list = source.medications === null ? null : asList(source.medications);
    if (source.medications === null || list !== undefined) {
      maybeApply("medications", list === undefined ? null : list);
    }
  }
  if (Object.prototype.hasOwnProperty.call(source, "demographics")) {
    if (source.demographics === null) {
      maybeApply("demographics", null);
    } else {
      const demo = asDemographics(source.demographics);
      if (demo) maybeApply("demographics", demo);
    }
  }

  return next;
}

export function applyCmrValuesToKnownRecord(current, cmrValues, sourceId) {
  if (!cmrValues || typeof cmrValues !== "object") return current || emptyKnownRecord();
  const payload = { sourceId, obtainedAt: Date.now() };
  if (Object.prototype.hasOwnProperty.call(cmrValues, "allergies")) {
    payload.allergies = cmrValues.allergies;
  }
  if (Object.prototype.hasOwnProperty.call(cmrValues, "medications")) {
    payload.medications = cmrValues.medications;
  }
  const demo = asDemographics(cmrValues.demographics || cmrValues);
  if (demo) payload.demographics = demo;
  return applyKnownRecordSource(current || emptyKnownRecord(), payload);
}

/**
 * Map redeemHealthRecordShareToken `record` into the unified path.
 * Null section (not in allowedSections) is omitted so it cannot wipe a
 * newer/other source. visitStartClarifyingQuestions is intentionally ignored.
 */
export function applyShareRedeemToKnownRecord(current, redeemed) {
  const record = redeemed?.record && typeof redeemed.record === "object"
    ? redeemed.record
    : {};
  const payload = {
    sourceId: KNOWN_RECORD_SOURCES.SHARE_CODE,
    obtainedAt: Date.now(),
  };
  if (record.allergies != null) payload.allergies = record.allergies;
  if (record.medications != null) payload.medications = record.medications;
  const demo = asDemographics({
    ...(record.demographics || {}),
    displayName: redeemed?.patientName,
    dateOfBirth: record.demographics?.dateOfBirth || redeemed?.patientDob,
  });
  if (demo) payload.demographics = demo;
  return applyKnownRecordSource(current || emptyKnownRecord(), payload);
}

export function knownList(record, field) {
  const slot = record?.[field];
  if (!slot || slot.value == null) return null;
  return Array.isArray(slot.value) ? slot.value : null;
}

export function hasKnownList(record, field) {
  return knownList(record, field) != null;
}

/**
 * Record → VisitStartRecordList row. These replaced formatAllergyLine /
 * formatMedicationLine, which flattened a record into one comma-joined string
 * because it was going to be a bullet inside an Alert. A list row has two slots:
 * the thing, and what is known about it.
 */
export function allergyRecordRow(row, index = 0) {
  const allergen = String(row?.allergen || row?.name || "").trim();
  if (!allergen) return null;
  return {
    key: `${allergen}-${index}`,
    primary: allergen,
    secondary: String(row?.reaction || "").trim(),
  };
}

export function medicationRecordRow(row, index = 0) {
  const name = String(row?.name || row?.medicationName || "").trim();
  if (!name) return null;
  const dosage = String(row?.dosage || "").trim();
  const frequency = String(row?.frequency || "").trim();
  return {
    key: `${name}-${index}`,
    primary: name,
    secondary: [dosage, frequency].filter(Boolean).join(", "),
  };
}

/** Both builders drop rows with nothing in the primary slot. */
export function toRecordRows(list, build) {
  return (Array.isArray(list) ? list : []).map(build).filter(Boolean);
}

export function isNkdaList(allergies) {
  if (!Array.isArray(allergies) || allergies.length !== 1) return false;
  const label = String(allergies[0]?.allergen || allergies[0]?.name || "")
    .trim()
    .toLowerCase();
  return label.includes("no known") || label === "nkda";
}

export function countQuestionsInAnswer(completion) {
  const answer = completion?.answer;
  if (!answer || typeof answer !== "object") return 1;
  if (Array.isArray(answer.answers) && answer.answers.length) {
    return answer.answers.length;
  }
  if (Array.isArray(answer.turns)) {
    const n = answer.turns.filter((t) => t?.speaker === "user").length;
    return n || 1;
  }
  return 1;
}

export function knownDemographics(record) {
  const slot = record?.demographics;
  if (!slot || slot.value == null || typeof slot.value !== "object") return null;
  return slot.value;
}

export function profileFromKnownRecord(record, sessionProfile = {}) {
  const demo = record?.demographics?.value || {};
  const name =
    String(sessionProfile.name || "").trim() ||
    String(demo.displayName || "").trim() ||
    [demo.firstName, demo.lastName].filter(Boolean).join(" ").trim();
  const dateOfBirth =
    String(sessionProfile.dateOfBirth || "").trim() ||
    String(demo.dateOfBirth || "").trim();
  return { name, dateOfBirth };
}
